import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const DocumentPreview = ({ document, onClose }) => {
  const [zoomLevel, setZoomLevel] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [annotations, setAnnotations] = useState([]);

  const mockAnnotations = [
    {
      id: 1,
      page: 1,
      x: 200,
      y: 150,
      type: 'comment',
      content: 'This clause needs review by senior partner',
      author: 'Sarah Wanjiku',
      date: '2024-01-15',
      resolved: false
    },
    {
      id: 2,
      page: 1,
      x: 300,
      y: 250,
      type: 'highlight',
      content: 'Important section for client discussion',
      author: 'John Kiprotich',
      date: '2024-01-14',
      resolved: true
    }
  ];

  const totalPages = 5; // Mock total pages

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 50));
  };

  const handlePageChange = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const addAnnotation = (x, y, type) => {
    const newAnnotation = {
      id: Date.now(),
      page: currentPage,
      x,
      y,
      type,
      content: '',
      author: 'Current User',
      date: new Date().toISOString().split('T')[0],
      resolved: false
    };
    setAnnotations(prev => [...prev, newAnnotation]);
  };

  const getFilePreview = () => {
    const fileType = document.type.toLowerCase();
    
    if (fileType === 'pdf') {
      return (
        <div className="relative bg-white border border-border rounded-lg overflow-hidden">
          <iframe
            src={document.url}
            className="w-full h-full"
            style={{ 
              transform: `scale(${zoomLevel / 100})`,
              transformOrigin: 'top left',
              width: `${10000 / zoomLevel}%`,
              height: `${10000 / zoomLevel}%`
            }}
            title={document.name}
          />
          
          {/* Annotations Overlay */}
          {showAnnotations && mockAnnotations
            .filter(annotation => annotation.page === currentPage)
            .map(annotation => (
              <div
                key={annotation.id}
                className={`absolute w-4 h-4 rounded-full cursor-pointer ${
                  annotation.type === 'comment' ? 'bg-warning' : 'bg-primary'
                } ${annotation.resolved ? 'opacity-50' : ''}`}
                style={{
                  left: `${annotation.x * (zoomLevel / 100)}px`,
                  top: `${annotation.y * (zoomLevel / 100)}px`
                }}
                title={annotation.content}
              />
            ))}
        </div>
      );
    }

    if (['jpg', 'jpeg', 'png', 'gif'].includes(fileType)) {
      return (
        <div className="flex items-center justify-center bg-secondary-50 rounded-lg">
          <img
            src={document.url}
            alt={document.name}
            className="max-w-full max-h-full object-contain"
            style={{ transform: `scale(${zoomLevel / 100})` }}
          />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-full bg-secondary-50 rounded-lg">
        <div className="text-center">
          <Icon name="FileX" size={64} className="mx-auto text-secondary-400 mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">Preview Not Available</h3>
          <p className="text-text-secondary mb-4">
            This file type cannot be previewed in the browser
          </p>
          <button
            onClick={() => window.open(document.url, '_blank')}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-smooth"
          >
            Download to View
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-dialog w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-4">
            <Icon name={document.type === 'pdf' ? 'FileText' : 'File'} size={24} className="text-primary" />
            <div>
              <h2 className="font-heading font-semibold text-text-primary">{document.name}</h2>
              <p className="text-sm text-text-secondary">
                Version {document.version} • {document.size} • Modified {new Date(document.lastModified).toLocaleDateString('en-GB')}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.open(document.url, '_blank')}
              className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth"
              title="Download"
            >
              <Icon name="Download" size={20} />
            </button>
            <button
              className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth"
              title="Print"
            >
              <Icon name="Printer" size={20} />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-secondary-50">
          <div className="flex items-center gap-4">
            {/* Zoom Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleZoomOut}
                disabled={zoomLevel <= 50}
                className="p-2 hover:bg-secondary-200 rounded-lg transition-smooth disabled:opacity-50"
              >
                <Icon name="ZoomOut" size={16} />
              </button>
              <span className="text-sm font-medium min-w-16 text-center">{zoomLevel}%</span>
              <button
                onClick={handleZoomIn}
                disabled={zoomLevel >= 200}
                className="p-2 hover:bg-secondary-200 rounded-lg transition-smooth disabled:opacity-50"
              >
                <Icon name="ZoomIn" size={16} />
              </button>
            </div>

            {/* Page Navigation */}
            {document.type === 'pdf' && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="p-2 hover:bg-secondary-200 rounded-lg transition-smooth disabled:opacity-50"
                >
                  <Icon name="ChevronLeft" size={16} />
                </button>
                <span className="text-sm font-medium">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="p-2 hover:bg-secondary-200 rounded-lg transition-smooth disabled:opacity-50"
                >
                  <Icon name="ChevronRight" size={16} />
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Annotation Tools */}
            <button
              onClick={() => setShowAnnotations(!showAnnotations)}
              className={`p-2 rounded-lg transition-smooth ${
                showAnnotations ? 'bg-primary text-white' : 'hover:bg-secondary-200'
              }`}
              title="Toggle Annotations"
            >
              <Icon name="MessageSquare" size={16} />
            </button>
            
            <button
              className="p-2 hover:bg-secondary-200 rounded-lg transition-smooth"
              title="Add Comment"
            >
              <Icon name="Plus" size={16} />
            </button>
            
            <button
              className="p-2 hover:bg-secondary-200 rounded-lg transition-smooth"
              title="Highlight"
            >
              <Icon name="Highlighter" size={16} />
            </button>
          </div>
        </div>

        {/* Preview Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Document Preview */}
          <div className="flex-1 p-4 overflow-auto bg-secondary-100">
            <div className="max-w-4xl mx-auto">
              {getFilePreview()}
            </div>
          </div>

          {/* Annotations Panel */}
          {showAnnotations && (
            <div className="w-80 border-l border-border bg-surface overflow-y-auto">
              <div className="p-4">
                <h3 className="font-medium text-text-primary mb-4">Annotations</h3>
                
                <div className="space-y-4">
                  {mockAnnotations.map(annotation => (
                    <div
                      key={annotation.id}
                      className={`p-3 border rounded-lg ${
                        annotation.resolved ? 'border-success bg-success-50' : 'border-border'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Icon 
                            name={annotation.type === 'comment' ? 'MessageSquare' : 'Highlighter'} 
                            size={14} 
                            className={annotation.type === 'comment' ? 'text-warning' : 'text-primary'}
                          />
                          <span className="text-xs text-text-secondary">Page {annotation.page}</span>
                        </div>
                        <button
                          className="text-xs text-text-secondary hover:text-text-primary"
                          onClick={() => {
                            // Toggle resolved status
                            console.log('Toggle resolved:', annotation.id);
                          }}
                        >
                          {annotation.resolved ? 'Resolved' : 'Resolve'}
                        </button>
                      </div>
                      
                      <p className="text-sm text-text-primary mb-2">{annotation.content}</p>
                      
                      <div className="text-xs text-text-secondary">
                        {annotation.author} • {new Date(annotation.date).toLocaleDateString('en-GB')}
                      </div>
                    </div>
                  ))}
                </div>

                {mockAnnotations.length === 0 && (
                  <div className="text-center py-8">
                    <Icon name="MessageSquare" size={32} className="mx-auto text-secondary-400 mb-2" />
                    <p className="text-sm text-text-secondary">No annotations yet</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;